import { Link, useParams, useNavigate } from 'react-router-dom';
import {
  QueryClient,
  QueryClientProvider,
  useQuery,
} from '@tanstack/react-query';
import Card from 'components/card';
import Button from 'components/button';
import SimpleTable from 'components/simpleTable';
import Header from 'components/text/header';
import { useState } from 'react';
import PackageForm from './packageForm';
import UseApi from 'hooks/useApi';
import { HiArrowLeft, HiDocumentDuplicate } from 'react-icons/hi';
import Loader from 'components/loader';

interface ItineraryItem {
  time: string;
  activity: string;
}

interface ItineraryDay {
  day: number;
  items: ItineraryItem[];
}

interface PackageData {
  packageName: string;
  overview: string;
  tripType: string;
  startDate: string;
  endDate: string;
  totalSeats: number;
  availableSeats: number;
  price_double_sharing: number;
  price_triple_sharing: number;
  price_quad_sharing: number;
  endUserReferralDiscount: number;
  referrerCommission: number;
  itinerary: ItineraryDay[];
  additionalFacilities: string[];
  pictures: string[];
  mainImage: string;
  metadata: {
    isArchived: boolean;
    archivedOn?: Date;
  };
}

const queryClient = new QueryClient();

const PackageData = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getApi, patchApi, deleteApi } = UseApi();

  async function fetchData(id: string) {
    const response = await getApi(`packages/${id}`);
    return response.data;
  }

  const { data, isLoading, error } = useQuery({
    queryKey: ['data', id],
    queryFn: () => fetchData(id),
  });
  const [isFormVisible, setIsFormVisible] = useState(false);
  if (isLoading) return <Loader />;
  if (error) return <Header>An error has occurred: {error.message}</Header>;

  const {
    packageName,
    overview,
    tripType,
    startDate,
    endDate,
    totalSeats,
    availableSeats,
    price_double_sharing,
    price_triple_sharing,
    price_quad_sharing,
    endUserReferralDiscount,
    referrerCommission,
    itinerary,
    additionalFacilities,
    pictures,
    mainImage,
    metadata,
  } = data as PackageData;

  const archive = async () => {
    await patchApi(`packages/${id}/metadata`, {
      isArchived: true,
      archivedOn: new Date(),
    });
  };
  const remove = async () => {
    await deleteApi(`packages/${id}`);
  };
  const toggleVisibility = () => {
    setIsFormVisible(!isFormVisible);
  };
  
  const handleDuplicate = () => {
    navigate('/admin/package/duplicate', { state: { packageData: data, isDuplicate: true } });
  };

  const formatItinerary = () => {
    if (!itinerary || !itinerary.length) return 'No itinerary available';
    return itinerary
      .map(
        (day: ItineraryDay, _index: number) =>
          `Day ${day.day}: ${day.items
            .map((item: ItineraryItem) => `${item.time} - ${item.activity}`)
            .join(', ')}`,
      )
      .join(' | ');
  };

  const formatFacilities = () => {
    if (!additionalFacilities || !additionalFacilities.length) return 'None';
    return additionalFacilities.join(', ');
  };

  return (
    <div className="flex flex-col gap-3">
      <Link key={1} to={'/admin/package'}>
        <Button variant="gray" extraClasses="flex items-center gap-2 ">
          <HiArrowLeft />
          Back
        </Button>
      </Link>
      <Card extra="gap-2 p-4">
        {isFormVisible ? (
          <PackageForm
            type="Update"
            formValues={data}
            onSubmit={toggleVisibility}
          />
        ) : (
          <SimpleTable
            header="Package Details"
            data={[
              { key: 'Package Name', value: packageName },
              { key: 'Overview', value: overview },
              {
                key: 'Trip Type',
                value: tripType === 'Domestic' ? 'Domestic' : 'International',
              },
              { key: 'Start Date', value: startDate?.substring(0, 10) },
              { key: 'End Date', value: endDate?.substring(0, 10) },
              { key: 'Total Seats', value: totalSeats.toString() },
              { key: 'Available Seats', value: availableSeats.toString() },
              {
                key: 'Price Double Sharing',
                value: 'PKR ' + price_double_sharing,
              },
              {
                key: 'Price Triple Sharing',
                value: 'PKR ' + price_triple_sharing,
              },
              { key: 'Price Quad Sharing', value: 'PKR ' + price_quad_sharing },
              { 
                key: 'Referral Discount', 
                value: 'PKR ' + (endUserReferralDiscount || 0) 
              },
              { 
                key: 'Referrer Commission', 
                value: 'PKR ' + (referrerCommission || 0) 
              },
              { key: 'Itinerary', value: formatItinerary() },
              { key: 'Additional Facilities', value: formatFacilities() },
              {
                key: 'Main Image',
                value: mainImage ? 'Uploaded' : 'Not uploaded',
              },
              {
                key: 'Additional Pictures',
                value: (pictures?.length || 0).toString(),
              },
            ]}
          />
        )}
        <div className="flex justify-center gap-3">
          <Button onClick={toggleVisibility}>
            {isFormVisible ? 'Cancel' : 'Edit'}
          </Button>
          <Button onClick={handleDuplicate} variant="gray">
            <div className="flex items-center gap-2">
              <HiDocumentDuplicate />
              Duplicate
            </div>
          </Button>
          {!metadata?.isArchived && (
            <Button onClick={archive} variant="error">
              Archive
            </Button>
          )}
        </div>
      </Card>
    </div>
  );
};

const PackageDetails = (props: {}) => {
  return (
    <QueryClientProvider client={queryClient}>
      <PackageData />
    </QueryClientProvider>
  );
};

export default PackageDetails;
